import type { GridDates, HiringGridCoord } from "../types";
import { dayDateOf, formatGridDayDate } from "./gridDates";
import { DAY_KEYS } from "./weekStart";

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * What a coordinate needs to become a sentence: which week numbers the cycle runs, the dates behind
 * them, and where the department starts its week. Grouped rather than passed as three more positional
 * arguments — `dates` and `weekStartDay` are only ever correct together (see `dayDateOf`).
 */
export type CoordLabelContext = {
	weekNumbers: number[];
	dates: GridDates;
	weekStartDay: number;
};

/**
 * A grid coordinate as the user reads it: "Fri 26 Sep W1 7am-7pm".
 *
 * The shift is named by the SCHEDULE TEMPLATE's own hours — the same key the recurring-gaps grid is
 * keyed by — so a coordinate on a card and a row in the table are obviously the same shift. There is no
 * separate period vocabulary to keep in step.
 *
 * `weekNumbers` comes from the payload and is parallel to the displayed weeks, and every coordinate the
 * backend emits sits inside that window — so the index resolves and there is no fallback to invent.
 */
export const formatCoordLabel = (coord: HiringGridCoord, { weekNumbers, dates, weekStartDay }: CoordLabelContext, t: Translate): string =>
	t("hiring.coord.label", {
		day: t(`hiring.day.${DAY_KEYS[coord.dayIdx]}`),
		date: formatGridDayDate(dayDateOf(dates, coord.weekIdx, coord.dayIdx, weekStartDay)),
		shift: coord.shiftLabel,
		week: weekNumbers[coord.weekIdx],
	});

/**
 * A grid coordinate as a NURSE reads it: "Thu 11pm-7am".
 *
 * The same day and hours as `formatCoordLabel`, without the date or the `W1`/`W2` token — that is the schedule
 * template's own vocabulary and means nothing in a text message. Used for the SMS a Move card prefills,
 * where both ends have to name their day: a Move pairs two DAYS on the SAME shift, so a message built
 * from the shift labels alone read "move from 11pm-7am to 11pm-7am" and said nothing at all.
 *
 * Its wording is kept stable on purpose: this string goes out as an SMS to a nurse, so changing it
 * changes what staff receive — a product decision, not a display one.
 */
export const formatCoordForMessage = (coord: HiringGridCoord, t: Translate): string =>
	t("hiring.coord.messageLabel", { day: t(`hiring.day.${DAY_KEYS[coord.dayIdx]}`), shift: coord.shiftLabel });
